import { Box, SimpleGrid, ChakraProvider, extendTheme, useToast } from "@chakra-ui/react";
import { useState } from "react";

import { UserDto } from "doxecure-commons/doxecureClientTypes";
import { WaitingRequestAction } from "doxecure-commons/doxecureTypes";
import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { InvoicesCard } from "@components/SubcriptionCards/InvoicesCard";
import { MandateCard }  from "@components/SubcriptionCards/MandateCard";
import { PlanCard }     from "@components/SubcriptionCards/PlanCard";
import { UsersCard }    from "@components/SubcriptionCards/UsersCard";
import { Plan, Mandate } from "@components/SubcriptionCards/types";
import { config } from "@/config/config";
import { useMollie } from "@/contexts/MollieContext";
import { useSubscription } from "@hooks/useSubscription";
import { createCBMandate, updateMandate } from "@services/users.service";
import theme from "@/theme";
import { defaultErrorToast, defaultSuccessToast } from "@/utils/toast";

const specificTheme = extendTheme({
    fonts: {
        heading: "'DM Serif Display', Georgia, serif",
        body: "'DM Sans', system-ui, sans-serif",
    },
    colors: {
        ...theme.colors,
        brand: {
            50: "#f0f4ff", 100: "#dce6ff", 500: "#3354f4",
            600: "#2441d9", 700: "#1a30b0", 900: "#0d1a5c",
        },
        surface: { base: "#F7EBE8", card: "#ffffff", subtle: "#f1f4fb" },
    },
    styles: {
        global: { 
            ...theme.global,
            body: { 
                bg: "surface.base", 
                color: "gray.800"
            }
        }
    },
    components: { 
        Button: { 
            baseStyle: { 
                fontWeight: "500", 
                borderRadius: "10px"
            }
        }
    },
});

interface SubscriptionTabProps {
    connectedUser?: Nullable<UserDto>;
    pricePerExtraUser?: number;
}

export const SubscriptionTab = (props: SubscriptionTabProps) => {
    const { connectedUser, pricePerExtraUser } = props;
    const { data: activeSubscription, requestCancelSubscription, cancelTerminationRequest, startSubscriptionImmediately } = useSubscription();
    const { mollie, ready } = useMollie();
    const toast = useToast();
    const [mandate, setMandate] = useState<Nullable<Mandate>>(connectedUser?.corporations?.first()?.mandate as Mandate);

    const corporation           = connectedUser?.corporations?.first();
    const isTrialPeriod         = $ok(corporation?.trialPeriod?.end);
    const waitingRequestAction  = activeSubscription?.requestWaintingActionInfos?.type as WaitingRequestAction;
    const maxUsers              = isTrialPeriod ? corporation?.trialPeriod?.extraUsers ?? 1 : corporation?.maxUsers ?? -1;

    const handleCardSubmit = async (cbToken: string): Promise<void> => {
        const checkoutURL = await createCBMandate(corporation!.apid, cbToken);
        if ($ok(checkoutURL)) {
            window.location.href = checkoutURL!;
        }
    };
    
    const handleMandateSubmit = async (consumerAccount: string, consumerName: string): Promise<void> => {
        const corpDTO = await updateMandate(corporation!.apid, consumerName, consumerAccount);
        if ($ok(corpDTO)) { 
            setMandate(corpDTO.mandate);
            toast(defaultSuccessToast("Nous avons pris en compte votre nouveau mandat SEPA"));
        }
    };

    function handleChangePlan(plan: Plan): void {
        // setSub((s) => ({ ...s, plan, isTrial: false }));
    }

    function handleStartNow(): void {
        try {
            startSubscriptionImmediately.mutate();
            toast(defaultErrorToast(`Votre abonnement a bien démarré. Vous avez un crédit de ${activeSubscription?.signatureCount.subscriptionMax} signatures`));
        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de l'activation de votre abonnement"));
        }
    }

    function handleTerminate(): void {
        try {
            requestCancelSubscription.mutate();
            toast(defaultErrorToast("La demande de ésiliation a bien été prise en compte"));
        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de la résiliation de votre abonnement"));
        }
    }

    function handleCancelTerminationRequest(): void {
        try {
            cancelTerminationRequest.mutate();
            toast(defaultErrorToast("L'annulation de la résiliation a bien été prise en compte"));
        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de la demande d'annulation de la résiliation"));
        }
    }

    function handleAddUsers(count: number): void {
        // setSub((s) => ({ ...s, usersAuthorized: s.usersAuthorized + count }));
    }

    function handleRemoveUsers(count: number): void {
//         setSub((s) => ({
//             ...s,
//             usersAuthorized: Math.max(s.plan.usersIncluded, s.usersAuthorized - count),
//         }));
    }

    return (
        <ChakraProvider theme={specificTheme}>
            <Box bg="surface.base" px={{ base: 4, md: 8, xl: 10 }} py={10} fontFamily="body">
                <SimpleGrid columns={{ base: 1, lg: 2}} spacing={5} mb={5}>
                    <PlanCard
                        connectedUser={connectedUser}
                        subscription={activeSubscription}
                        waitingRequestAction={waitingRequestAction}
                        extraUsers={0}
                        pricePerExtraUser={pricePerExtraUser ?? 0}
                        onChangePlan={handleChangePlan}
                        onStartNow={handleStartNow}
                        onTerminate={handleTerminate}
                        onCancelTerminationRequest={handleCancelTerminationRequest}
                    />
                    <UsersCard
                        activeUsers={1}
                        maxUsers={maxUsers}
                        isTrialPeriod={isTrialPeriod}
                        pricePerExtraUser={pricePerExtraUser ?? 0}
                        onAddUsers={handleAddUsers}
                        onRemoveUsers={handleRemoveUsers}
                    />
                    <MandateCard
                        mandate={mandate}
                        isTerminationRequested={waitingRequestAction === "cancel-subscription"}
                        companyName={config.app.title}
                        testMode={config.app.mollie.testmode}
                        mollie={mollie}
                        mollieReady={ready}
                        onCardSubmit={handleCardSubmit}
                        onMandateSubmit={handleMandateSubmit}
                    />
                </SimpleGrid>

                <InvoicesCard />
            </Box>
        </ChakraProvider>
    );
};